import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { text_colorTextSecondary } from 'ext-ui/style/colors'
import React, { useEffect, useId, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import CurrencyDropdown from 'src/components/CurrencyDropdown/CurrencyDropdown'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import { ExtendCurrency } from 'src/enums'
import Crncy, { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { selectNullSafeCurrencyFromState } from 'src/redux/selectors/auth.selectors'
import { css } from 'styled-components'

interface AmountInputFieldProps {
  autoComplete?: string
  baseCurrency?: ExtendCurrency
  baseValue?: number
  className?: string
  currency?: ExtendCurrency
  disabled?: boolean
  errors?: Array<React.ReactElement | string>
  fieldLabel?: React.ReactNode
  isTextarea?: boolean
  maxValue: number
  name?: string
  onChange: (value: number | undefined) => void
  showUpdates?: boolean
  type?: string
  value?: number
  onCurrencyChange?: (value: ExtendCurrency) => void
  isRequest: boolean
  unitOfLength?: string
  placeholder?: string
  optional?: boolean
  tooltip?: React.ReactElement
  static?: boolean
  testId?: string
  integerOnly?: boolean
  /** Marks the control required for assistive tech (`aria-required`). Pair with
   *  a visible cue in `fieldLabel` — this does not render one. */
  required?: boolean
}

const AmountInputField: React.FC<AmountInputFieldProps> = (
  props,
): React.ReactNode => {
  const translate = useTranslate()

  const defaultCurrency = useSelector(selectNullSafeCurrencyFromState)
  const currencyFormatter = useCurrencyFormatter()

  /*
   * Unique per instance. The input previously hardcoded id="amountInput", so
   * two amount fields on one page (CreditCardAndAmountInputBox renders several)
   * emitted duplicate DOM ids, and the FieldLabel was never associated with the
   * control at all — a screen reader got no name, no required state and no
   * validation description for the field. src/AGENTS.md makes that a hard rule,
   * not a nice-to-have.
   */
  const generatedId = useId()
  const inputId = `amount-input-${generatedId}`
  const errorsId = `amount-errors-${generatedId}`

  const inputValue = props.value !== undefined ? props.value : ''

  const [showErrors, setShowErrors] = useState(props.errors !== undefined)
  useEffect((): void => {
    setShowErrors(props.errors !== undefined)
  }, [props.errors])

  const hasVisibleErrors = showErrors && props.errors !== undefined

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const initialString = props.integerOnly
      ? e.target.value.replace(/\./g, '')
      : e.target.value

    /*
     * Cap the typed precision. `unitOfLength` already truncated to 1 decimal;
     * every other (currency) use truncated to nothing at all, so a money field
     * accepted "9999999.444444444" — sub-cent precision it can never render.
     * On blur the field formats to 2 decimals, so the value SHOWN and the value
     * HELD silently disagreed, and the extra precision travelled on to whatever
     * consumed it. Every consumer of this component is a money field
     * (budgets, card limits, expense amounts, low-balance alerts, receipt-rule
     * and manager-review thresholds), and none of them can represent a fraction
     * of a cent.
     */
    const maxDecimals = props.integerOnly ? 0 : props.unitOfLength ? 1 : 2
    const decimalIndex = initialString.indexOf('.')
    const formattedString =
      decimalIndex >= 0
        ? initialString.slice(0, decimalIndex + 1 + maxDecimals)
        : initialString

    const parsedValue = parseFloat(formattedString)
    const value =
      parsedValue !== undefined && !isNaN(parsedValue)
        ? Math.min(parsedValue, props.maxValue)
        : undefined

    /*
     * Hide a stale validation message when the VALUE changes, not on every
     * keystroke. The parent re-supplies `errors` for the new value and the
     * effect above restores them.
     *
     * Keystroke-based hiding lost the message outright once a keystroke stopped
     * changing the value — typing past `maxValue` clamps to the same number, so
     * the parent's state setter is a no-op, React skips the re-render, the
     * `errors` prop never changes identity, and the effect that would restore
     * the message never re-runs. The field then sat over its limit with the
     * error gone. Gating on a real value change keeps the ordinary
     * clear-as-you-fix-it behaviour for every other consumer.
     */
    if (value !== props.value) {
      setShowErrors(false)
    }

    props.onChange(value)
  }

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (props.integerOnly && e.key === '.') {
      e.preventDefault()
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (document.activeElement === inputRef.current) {
        inputRef.current?.blur()
      }
      return
    }
  }

  const [isInputFocused, setIsInputFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const onInputFocus = (): void => {
    setIsInputFocused(true)
  }
  const onInputBlur = (): void => {
    setIsInputFocused(false)
  }

  useEffect((): void => {
    if (isInputFocused) {
      inputRef.current?.select()
    }
  }, [isInputFocused])

  const formattedInput = (val: number | undefined): string => {
    return val !== undefined && props.currency !== undefined
      ? currencyFormatter({
          amount: val,
          currency: props.currency,
          forceHideCurrency: true,
          maximumFractionDigits: props.integerOnly ? 0 : 2,
        })
      : ''
  }

  const lengthFormattedInput = (val: number | undefined): string => {
    return val !== undefined && props.unitOfLength
      ? val === Math.round(val)
        ? val.toString()
        : val.toFixed(1)
      : ''
  }

  const isUpdated = !!props.showUpdates && props.baseValue !== props.value

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;

        input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          appearance: none;
          margin: 0;
        }

        input[type='number'] {
          -moz-appearance: textfield;
        }
      `}
    >
      {props.fieldLabel !== undefined && (
        <FieldLabel
          css={css`
            z-index: 1;
            display: flex;
            align-items: center;
          `}
          htmlFor={inputId}
          isUpdated={isUpdated}
          dontHideOverflow
        >
          {isUpdated ? (
            props.baseValue !== undefined ? (
              <>
                {props.unitOfLength !== undefined &&
                  translate('${fieldLabel} (was "${baseValue}")', {
                    fieldLabel: props.fieldLabel,
                    baseValue: props.baseValue.toString(),
                  })}
                {props.baseCurrency !== undefined &&
                  translate('${fieldLabel} (was ${baseValue})', {
                    fieldLabel: props.fieldLabel,
                    baseValue: (
                      <Crncy
                        currency={props.baseCurrency}
                        amount={props.baseValue}
                      />
                    ),
                  })}
              </>
            ) : (
              <>{props.fieldLabel}</>
            )
          ) : (
            props.fieldLabel
          )}
          {props.tooltip !== undefined && !props.static && props.tooltip}
        </FieldLabel>
      )}
      {props.static && (
        <div>
          {props.currency && props.value ? (
            <Crncy currency={props.currency} amount={props.value} />
          ) : (
            '--'
          )}
        </div>
      )}
      {!props.static && (
        <>
          <span
            css={css`
              display: flex;
              flex-direction: row;
              align-items: center;
              caret-color: ${themeConstants.colors.cta};
              flex-grow: 2;
              width: 100%;
              border-radius: 6px;
              height: 50px;
              border: 1px solid ${styleConstants.colors.gray_medium};
              background-color: ${styleConstants.colors.gray_lightest};

              & > * {
                background-color: inherit;
                border-radius: inherit;
                border: 0px;
                ${getFontCss(
                  14,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.regular,
                )};

                /*
                 * Placeholder colour is the DS secondary-text token, not the
                 * legacy grey. styleConstants.colors.gray_dark (#B3B7BE)
                 * measures 2.0:1 on this field's background — below the 4.5:1
                 * WCAG 2.1 AA minimum that src/AGENTS.md makes mandatory.
                 * text_colorTextSecondary (#697080) measures 4.96:1, and is
                 * what every ext-ui input already uses for its placeholder, so
                 * this also stops amount fields reading lighter than the text
                 * fields beside them.
                 */
                &::placeholder {
                  ${getFontCss(
                    14,
                    text_colorTextSecondary,
                    styleConstants.fontFamilies.regular,
                  )};
                }
              }

              & > :first-child {
                border-right: inherit;
                border-top-right-radius: 0px;
                border-bottom-right-radius: 0px;
              }

              &:focus,
              &:hover {
                background-color: ${styleConstants.colors.white};
                border: 1px solid ${themeConstants.colors.cta};
              }

              ${isUpdated &&
              css`
                border: 1px solid ${themeConstants.colors.highlight};

                &:focus-within,
                &:hover {
                  border: 1px solid ${themeConstants.colors.highlight};
                }
              `}

              ${showErrors &&
              css`
                border: 1px solid ${styleConstants.colors.red};
              `}
            `}
          >
            {props.isRequest && props.onCurrencyChange ? (
              <CurrencyDropdown
                className={'currency-input'}
                css={css`
                  min-width: 83px;
                  padding: 0;
                  border: 0;
                  border-right: inherit;
                  border-radius: inherit;
                `}
                selectedValue={
                  props.currency !== undefined
                    ? props.currency
                    : defaultCurrency
                }
                chooseOption={props.onCurrencyChange}
                smallDisplay={true}
                rootContainerCss={css`
                  border: 0;
                  background-color: inherit;
                  height: 100%;
                  min-height: 48px;

                  &:focus-within,
                  &:hover {
                    border: 0;
                  }
                `}
              />
            ) : (
              <div
                css={css`
                  width: 68px;
                  height: 100%;
                  display: flex;
                  align-items: center;
                  padding: 0px 20px;
                  color: ${isUpdated && themeConstants.colors.highlight};
                `}
              >
                {props.unitOfLength
                  ? props.unitOfLength
                  : props.currency
                    ? props.currency
                    : defaultCurrency}
              </div>
            )}
            <input
              css={css`
                height: 100%;
                width: 100%;
                display: flex;
                align-items: center;
                padding: 0px 20px;
                color: ${isUpdated && themeConstants.colors.highlight};
              `}
              ref={inputRef}
              className={'input'}
              placeholder={
                props.placeholder !== undefined ? props.placeholder : '0'
              }
              value={
                isInputFocused
                  ? inputValue
                  : props.unitOfLength
                    ? lengthFormattedInput(props.value)
                    : formattedInput(props.value)
              }
              type={isInputFocused ? 'number' : 'text'}
              step={props.integerOnly ? 1 : 0.01}
              onBlur={onInputBlur}
              onChange={onChange}
              disabled={props.disabled}
              onFocus={onInputFocus}
              autoComplete={props.autoComplete || 'off'}
              name={props.name}
              inputMode={props.integerOnly ? 'numeric' : 'decimal'}
              onKeyDown={handleKeyPress}
              onWheel={(e): void => e.currentTarget.blur()}
              id={inputId}
              aria-required={props.required || undefined}
              aria-invalid={hasVisibleErrors || undefined}
              aria-describedby={hasVisibleErrors ? errorsId : undefined}
              data-testid={
                props.testId
                  ? props.testId
                  : props.name
                    ? `${props.name}-input`
                    : 'amount-input'
              }
            />
          </span>
          {showErrors && props.errors !== undefined && (
            <div id={errorsId}>
              <ErrorList errors={props.errors} />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default React.memo(AmountInputField)
